import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, IconButton, InputAdornment } from "@mui/material";
import Typography from "@mui/material/Typography";
import { backgroundColor, textColor } from "../../../assets/styles/colors";
import { appConfig } from "../../../variables/appConfig";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { alertService, EmailInput, FirstNameInput, getNumber, Input, LastNameInput, PhoneInput, PhoneIti, removeEmptyFields, useContainer, } from "common-frontend-components";
import Dialog from "@mui/material/Dialog";
import Slide from '@mui/material/Slide';
import { textFieldStyle } from "../../../assets/styles/styles";
import { useContactValidationSchema } from "../../../validations/schemas/contactValidation";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { callCreateContactAsync } from "../../../apis/contacts";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
// import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { globalAppConfigStore } from "../../../stores/appConfig/globalAppConfigStore";
import { sendContactInfoStore } from "../../../stores/sendContactInfoStore";
const Transition = forwardRef((props, ref) => _jsx(Slide, { direction: "left", ref: ref, ...props }, void 0));
const defaultValues = {
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    message: "",
};
const IconBox = ({ sx = {}, ...props }) => _jsx(Box, { sx: {
        position: 'relative',
        padding: '20px 0',
        display: 'flex',
        ...sx
    }, ...props }, void 0);
const Icon = ({ sx = {}, ...props }) => _jsx(Box, { sx: {
        width: '60px',
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        border: '1px solid #fff',
        ...sx
    }, ...props }, void 0);
const TextContainer = ({ sx = {}, ...props }) => _jsx(Box, { sx: {
        display: 'flex',
        marginLeft: '20px',
        flexDirection: 'column',
        maxWidth: '25ch',
        ...sx
    }, ...props }, void 0);
const Text = ({ sx = {}, ...props }) => _jsx(Typography, { sx: {
        fontWeight: 300,
        fontSize: '.9rem',
        padding: 0,
        color: textColor,
        ...sx
    }, color: "text.secondary", gutterBottom: true, ...props }, void 0);
const Header = ({ sx = {}, ...props }) => _jsx(Typography, { sx: {
        fontWeight: 500,
        fontSize: '1.5rem',
        padding: 0,
        color: textColor,
        ...sx
    }, color: "text.secondary", gutterBottom: true, ...props }, void 0);
const FormContainer = ({ children }) => {
    return appConfig.address || appConfig.phone || appConfig.email
        ? _jsx(Box, { sx: { width: '100%' }, children: children }, void 0)
        : _jsx(Grid, { container: true, justifyContent: 'center', children: children }, void 0);
};
export const ContactForm = ({ background, closeButton = null }) => {
    const contactSent = sendContactInfoStore.useContactSent();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const phoneContainer = useContainer();
    const validationSchema = useContactValidationSchema(phoneContainer);
    const { register, handleSubmit, errors } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues,
    });
    const onSubmit = handleSubmit((fields) => {
        if (isSubmitting)
            return;
        setIsSubmitting(true);
        // get special fields
        fields.phone = getNumber(phoneContainer, '');
        // @ts-ignore
        const contact = removeEmptyFields(fields);
        callCreateContactAsync(contact)
            .then(() => {
            alertService.success("Thank you for reaching out to us ☺ We will contact you shortly through email or phone");
            sendContactInfoStore.setContactSent();
            sendContactInfoStore.closeContact();
        })
            .catch(alertService.error)
            .finally(() => {
            setIsSubmitting(false);
        });
    });
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
@keyframes Contact-fading {
  0%   { background: #00000000; }
  10%  { background: #00000000; }
  40%  { background: #000000af; }
  60%  { background: #000000af; }
  90%  { background: #00000000; }
  100% { background: #00000000; }
}
        ` }, void 0), _jsx(Box, { component: 'section', sx: {
                    background,
                    backgroundSize: 'cover',
                    height: '100%',
                    width: '100%',
                }, children: _jsxs(Box, { sx: {
                        ...textFieldStyle,
                        position: 'relative',
                        padding: '50px 100px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        width: '100%',
                        background: 'transparent',
                        animation: 'Contact-fading 30s infinite',
                    }, children: [_jsxs(Box, { sx: {
                                maxWidth: '800px',
                                textAlign: 'center',
                            }, children: [_jsx(Typography, { sx: { fontSize: '2rem', padding: 2, paddingBottom: 1, paddingTop: 3, color: 'white', fontWeight: 500 }, color: "text.secondary", gutterBottom: true, children: "Contact Us" }, void 0), closeButton] }, void 0), _jsxs(Box, { sx: {
                                maxWidth: '600px',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: '30px',
                            }, children: [(appConfig.address || appConfig.phone || appConfig.email) ? _jsxs(Box, { sx: {
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }, children: [appConfig.address ? _jsxs(IconBox, { children: [_jsx(Icon, { children: _jsx(LocationOnIcon, {}, void 0) }, void 0), _jsxs(TextContainer, { children: [_jsx(Header, { children: "Address" }, void 0), _jsx(Text, { children: appConfig.address || "" }, void 0)] }, void 0)] }, void 0) : null, appConfig.phone ? _jsxs(IconBox, { children: [_jsx(Icon, { children: _jsx(PhoneIcon, {}, void 0) }, void 0), _jsxs(TextContainer, { children: [_jsx(Header, { children: "Phone" }, void 0), _jsx(Text, { children: appConfig.phone || "" }, void 0)] }, void 0)] }, void 0) : null, appConfig.email ? _jsxs(IconBox, { children: [_jsx(Icon, { children: _jsx(MailOutlineIcon, {}, void 0) }, void 0), _jsxs(TextContainer, { children: [_jsx(Header, { children: "Email" }, void 0), _jsx(Text, { children: appConfig.email || "" }, void 0)] }, void 0)] }, void 0) : null] }, void 0) : null, _jsx(FormContainer, { children: contactSent
                                        ?
                                            _jsx(Box, { component: 'p', sx: { ...textFieldStyle, fontSize: '1.1rem', maxWidth: '30ch', textAlign: 'center' }, children: "Thank you for reaching out to us \u263A We will contact you shortly through email or phone" }, void 0)
                                        :
                                            _jsxs(Box, { component: 'form', onSubmit: onSubmit, sx: {
                                                    maxWidth: '38ch',
                                                    '& .MuiTextField-root': { m: 1, width: '25ch' },
                                                }, children: [_jsx(FirstNameInput, { label: "first name", placeholder: "john", error: errors.firstName?.message, inputRef: register }, void 0), _jsx(LastNameInput, { InputProps: {
                                                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(AccountBoxIcon, {}, void 0) }, void 0)),
                                                        }, label: "last name", placeholder: "sanchaz", error: errors.lastName?.message, inputRef: register, InputLabelProps: { shrink: true } }, void 0), _jsx(EmailInput, { label: "email", placeholder: "john@example.com", error: errors.email?.message, inputRef: register }, void 0), _jsx(Box, { sx: {
                                                            '& .iti__arrow': {
                                                                marginLeft: '6px!important',
                                                                marginInlineStart: '0px',
                                                                borderLeft: '3px solid transparent!important',
                                                                borderRight: '3px solid transparent!important',
                                                                borderTop: '4px solid #555!important',
                                                            },
                                                            '&::before, ::after': {
                                                                borderColor: '#c6d0e8!important',
                                                            }
                                                        }, children: _jsx(PhoneIti, { container: phoneContainer, initialValue: '', id: "contact-phone", children: _jsx(PhoneInput, { label: "phone", error: errors.phone?.message, inputRef: register, id: "contact-phone" }, void 0) }, void 0) }, void 0), _jsx(Box, { sx: { paddingTop: 1 }, children: _jsx(Input, { InputLabelProps: { shrink: true }, error: !!errors.message?.message, helperText: errors.message?.message, inputRef: register, label: "type your message", name: "message", variant: 'outlined', multiline: true, rows: 2 }, void 0) }, void 0), _jsx(Button, { type: 'submit', sx: { marginLeft: 1, marginTop: '1.1rem' }, variant: isSubmitting ? undefined : 'outlined', children: isSubmitting ? _jsx(CircularProgress, {}, void 0) : 'Send' }, void 0)] }, void 0) }, void 0)] }, void 0)] }, void 0) }, void 0)] }, void 0));
};
export const ContactPopup = () => {
    const open = sendContactInfoStore.useOpen();
    const config = globalAppConfigStore.useComponent('contact-background');
    return (_jsx(Dialog, { onClose: sendContactInfoStore.closeContact, open: open, fullScreen: true, TransitionComponent: Transition, keepMounted: true, "aria-describedby": "contact-us", 
        // onClick={sendContactInfoStore.closeContact}
        PaperProps: { sx: { backgroundColor } }, children: _jsx(ContactForm, { closeButton: _jsx(IconButton, { color: "error", size: 'large', sx: { position: 'absolute', top: 2, right: 2 }, onClick: sendContactInfoStore.closeContact, children: _jsx(HighlightOffIcon, { sx: { width: 40, height: 40 } }, void 0) }, void 0), background: (config?.params?.background || "").replace("$url", config?.url || "") }, void 0) }, void 0));
};
//# sourceMappingURL=Contact.js.map